import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import useApi from "../../hooks/useApi";
import "./AgentStudents.css";

const initialForm = {
  full_name: "",
  email: "",
  contact: "",
  zipcode: "",
  dob: "",
  gender: "male",
  city_id: "",
  course_id: "",
  assigned_school_id: "",
  enrollment_for: "self",
  expected_start_date: "",
  time_slot: "",
  pref_female_driver: false,
  pref_nearby_only: true,
  pref_min_rating: "",
  preferred_car_type: "",
  admin_note: "",
  inc_sat_sun: false,
};

const toTimeLabel = (value) => String(value || "").substring(0, 5);
const toSlotValue = (start, end) => `${toTimeLabel(start)} - ${toTimeLabel(end)}`;
const normalizeSlotValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const parts = raw.split("-").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${toTimeLabel(parts[0])} - ${toTimeLabel(parts[1])}`;
  }
  return raw;
};
const formatSlotOptionLabel = (slot) => {
  const base = slot?.value || "";
  const capacity = Number(slot?.capacity || 0);
  const bookedCount = Number(slot?.booked_count || 0);
  if (capacity > 0) {
    return `${base} (${bookedCount}/${capacity})${slot?.booked ? " - Full" : ""}`;
  }
  return base;
};
const toDateInputValue = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return raw.split("T")[0];
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return "";
};

const getLatestAllowedDob = () => {
  const today = new Date();
  today.setFullYear(today.getFullYear() - 18);
  return today.toISOString().split("T")[0];
};

const isAtLeast18 = (dob) => {
  if (!dob) return false;
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 18;
};

const AgentStudents = () => {
  const { bearerAuth } = useApi();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const res = await bearerAuth("agent/students", "GET", { search });
      if (res?.data?.status) {
        setStudents(res.data.data || []);
      } else {
        setStudents([]);
      }
    } catch (err) {
      setStudents([]);
      Swal.fire("Error", err?.message || "Failed to load students", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadMeta = async () => {
    try {
      const [courseRes, cityRes] = await Promise.all([
        bearerAuth("agent/courses"),
        bearerAuth("agent/cities"),
      ]);
      setCourses(courseRes?.data?.data || []);
      setCities(cityRes?.data?.data || []);
    } catch (err) {
      setCourses([]);
      setCities([]);
      setTimeSlots([]);
    }
  };

  const loadAvailableTimeSlots = async ({ date, schoolId }) => {
    try {
      if (!date || !schoolId) {
        setTimeSlots([]);
        return;
      }

      const res = await bearerAuth("check-timeslots", "POST", {
        date,
        assignSchool: schoolId,
      });
      const rows = res?.data?.slots || res?.data?.data || [];
      const mapped = rows
        .map((slot) => {
          if (!slot?.start_time || !slot?.end_time) return null;
          return {
            value: toSlotValue(slot.start_time, slot.end_time),
            booked: !!slot.booked,
            booked_count: Number(slot.booked_count || 0),
            capacity: slot.capacity ?? null,
          };
        })
        .filter(Boolean);

      setTimeSlots(mapped);
    } catch (err) {
      setTimeSlots([]);
    }
  };

  const loadSchoolsByCity = async (cityId, prefState = form) => {
    try {
      if (!cityId) {
        setSchools([]);
        return;
      }
      const res = await bearerAuth("agent/schools", "GET", {
        city_id: cityId,
        pref_female_driver: prefState.pref_female_driver ? 1 : 0,
        pref_min_rating: prefState.pref_min_rating || undefined,
        preferred_car_type: prefState.preferred_car_type || undefined,
      });
      const list = res?.data?.data || [];
      setSchools(list);
      setForm((prev) => {
        const exists = list.some((s) => String(s.id) === String(prev.assigned_school_id));
        if (exists) return prev;
        if (list.length === 1) {
          return { ...prev, assigned_school_id: String(list[0].id), time_slot: "" };
        }
        return { ...prev, assigned_school_id: "", time_slot: "" };
      });
    } catch (err) {
      setSchools([]);
    }
  };

  const loadCoursesByCity = async (cityId) => {
    try {
      const res = await bearerAuth("agent/courses", "GET", cityId ? { city_id: cityId } : {});
      setCourses(res?.data?.data || []);
    } catch (err) {
      setCourses([]);
    }
  };

  const toDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
  };

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s) => {
      const e = s.latest_enrollment || {};
      const rowDate = toDateKey(e.expected_start_date || e.created_at || s.created_at);
      const matchesSearch =
        !q ||
        (s.full_name || s.username || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q) ||
        (s.contact || "").toLowerCase().includes(q) ||
        (e.course?.title || "").toLowerCase().includes(q) ||
        (e.driving_school?.name || "").toLowerCase().includes(q);
      const matchesFromDate = !fromDate || (rowDate && rowDate >= fromDate);
      const matchesToDate = !toDate || (rowDate && rowDate <= toDate);
      const matchesDate = matchesFromDate && matchesToDate;
      return matchesSearch && matchesDate;
    });
  }, [students, search, fromDate, toDate]);

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedStudents = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredStudents.slice(start, start + itemsPerPage);
  }, [filteredStudents, safePage]);

  useEffect(() => {
    loadStudents();
    loadMeta();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, fromDate, toDate]);

  useEffect(() => {
    if (!showModal || !form.city_id) return;
    loadSchoolsByCity(form.city_id, form);
  }, [showModal, form.city_id, form.pref_female_driver, form.pref_min_rating, form.preferred_car_type]);

  useEffect(() => {
    if (!showModal) return;
    if (!form.expected_start_date || !form.assigned_school_id) {
      setTimeSlots([]);
      return;
    }
    loadAvailableTimeSlots({
      date: form.expected_start_date,
      schoolId: form.assigned_school_id,
    });
  }, [showModal, form.expected_start_date, form.assigned_school_id]);

  const openAdd = () => {
    setEditingId(null);
    setForm(initialForm);
    setSchools([]);
    setTimeSlots([]);
    loadCoursesByCity("");
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await bearerAuth(`agent/students/${id}`);
      const data = res?.data?.data;
      if (!data) return;
      const enrollment = data.latest_enrollment || {};
      setEditingId(id);
      setForm({
        full_name: data.full_name || data.username || "",
        email: data.email || "",
        contact: data.contact || "",
        zipcode: data.zipcode || "",
        dob: enrollment.dob || "",
        gender: enrollment.gender || "male",
        city_id: data.city_id ? String(data.city_id) : "",
        course_id: enrollment.course_id || "",
        assigned_school_id: enrollment.assigned_school_id ? String(enrollment.assigned_school_id) : "",
        enrollment_for: enrollment.enrollment_for || "self",
        expected_start_date: toDateInputValue(enrollment.expected_start_date),
        time_slot: normalizeSlotValue(enrollment.time_slot || ""),
        pref_female_driver: Number(enrollment.pref_female_driver || 0) === 1,
        pref_nearby_only: enrollment.pref_nearby_only == null ? true : Number(enrollment.pref_nearby_only || 0) === 1,
        pref_min_rating: enrollment.pref_min_rating != null ? String(enrollment.pref_min_rating) : "",
        preferred_car_type: enrollment.preferred_car_type || "",
        admin_note: enrollment.admin_note || "",
        inc_sat_sun: Number(enrollment.inc_sat_sun || 0) === 1,
      });
      if (data.city_id) {
        await loadSchoolsByCity(data.city_id, {
          pref_female_driver: Number(enrollment.pref_female_driver || 0) === 1,
          pref_min_rating: enrollment.pref_min_rating != null ? String(enrollment.pref_min_rating) : "",
          preferred_car_type: enrollment.preferred_car_type || "",
        });
        await loadCoursesByCity(data.city_id);
      } else {
        setSchools([]);
      }
      setShowModal(true);
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to load student", "error");
    }
  };

  const saveStudent = async (e) => {
    e.preventDefault();
    if (!isAtLeast18(form.dob)) {
      Swal.fire("Validation Error", "Student must be at least 18 years old.", "warning");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...form,
        city_id: Number(form.city_id),
        course_id: Number(form.course_id),
        assigned_school_id: Number(form.assigned_school_id),
        time_slot: normalizeSlotValue(form.time_slot),
        inc_sat_sun: form.inc_sat_sun ? 1 : 0,
        pref_female_driver: form.pref_female_driver ? 1 : 0,
        pref_nearby_only: form.pref_nearby_only ? 1 : 0,
        pref_min_rating: form.pref_min_rating ? Number(form.pref_min_rating) : null,
        preferred_car_type: form.preferred_car_type || null,
      };

      if (editingId) {
        await bearerAuth(`agent/students/${editingId}`, "POST", payload);
      } else {
        await bearerAuth("agent/students", "POST", payload);
      }

      setShowModal(false);
      loadStudents();
      Swal.fire("Success", editingId ? "Student updated." : "Student added.", "success");
    } catch (err) {
      Swal.fire("Error", err?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  // Mobile card view for each student
  const MobileStudentCard = ({ student }) => {
    const e = student.latest_enrollment || {};
    return (
      <div className="agent-student-mobile-card mb-3 p-3 rounded">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div className="fw-semibold">{student.full_name || student.username || "-"}</div>
            <div className="text-muted small">{student.email || "-"}</div>
          </div>
          <Badge bg="info">{e.status || "Pending"}</Badge>
        </div>

        <div className="info-row">
          <span className="info-label">Contact:</span>
          <span className="info-value">{student.contact || "-"}</span>
        </div>

        <div className="info-row">
          <span className="info-label">City:</span>
          <span className="info-value">
            {student.city?.name ? `${student.city.name}${student.city.state_name ? `, ${student.city.state_name}` : ""}` : "-"}
          </span>
        </div>

        <div className="info-row">
          <span className="info-label">Course:</span>
          <span className="info-value">{e.course?.title || "-"}</span>
        </div>

        <div className="info-row">
          <span className="info-label">Per User:</span>
          <span className="info-value fw-semibold text-primary">INR {Number(student.estimated_commission_per_user || 0).toFixed(2)}</span>
        </div>

        <div className="info-row">
          <span className="info-label">School:</span>
          <span className="info-value">{e.driving_school?.name || "-"}</span>
        </div>

        <div className="info-row">
          <span className="info-label">Est. Date:</span>
          <span className="info-value">{e.expected_start_date || "-"}</span>
        </div>

        <div className="info-row">
          <span className="info-label">Est. Slot:</span>
          <span className="info-value">{e.time_slot || "-"}</span>
        </div>

        <div className="mt-3">
          <Button 
            size="sm" 
            variant="outline-primary" 
            className="w-100"
            onClick={() => openEdit(student.id)}
          >
            Edit Student
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="agent-students-container">
      {/* Header Card with Search */}
      <Card className="shadow-sm mb-3">
        <Card.Body className="d-flex flex-column flex-md-row gap-2 justify-content-between">
          <div className="d-flex flex-column flex-lg-row gap-2 flex-grow-1">
            <Form.Control
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadStudents()}
            />
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              max={toDate || undefined}
              style={{ maxWidth: 200 }}
            />
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || undefined}
              style={{ maxWidth: 200 }}
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-primary" onClick={loadStudents}>Search</Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setSearch("");
                setFromDate("");
                setToDate("");
              }}
              disabled={!search && !fromDate && !toDate}
            >
              Clear Filters
            </Button>
            <Button onClick={openAdd}>Add Student</Button>
          </div>
        </Card.Body>
      </Card>

      {/* Students List Card */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-center">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Loading students...</div>
            </div>
          ) : (
            <>
              <div className="d-none d-md-block agent-table-view">
                <Table responsive hover className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Contact</th>
                      <th>City</th>
                      <th>Course</th>
                      <th>Per User</th>
                      <th>School</th>
                      <th>Status</th>
                      <th>Est. Date</th>
                      <th>Est. Slot</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((s) => {
                      const e = s.latest_enrollment || {};
                      return (
                        <tr key={s.id}>
                          <td>{s.full_name || s.username || "-"}</td>
                          <td>{s.email || "-"}</td>
                          <td>{s.contact || "-"}</td>
                          <td>{s.city?.name ? `${s.city.name}${s.city.state_name ? `, ${s.city.state_name}` : ""}` : "-"}</td>
                          <td>{e.course?.title || "-"}</td>
                          <td className="fw-semibold">INR {Number(s.estimated_commission_per_user || 0).toFixed(2)}</td>
                          <td>{e.driving_school?.name || "-"}</td>
                          <td>{e.status || "-"}</td>
                          <td>{e.expected_start_date || "-"}</td>
                          <td>{e.time_slot || "-"}</td>
                          <td>
                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(s.id)}>
                              Edit
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {!paginatedStudents.length && (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-4">No students found.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-block d-md-none p-3">
                {paginatedStudents.length ? (
                  paginatedStudents.map((student) => (
                    <MobileStudentCard key={student.id} student={student} />
                  ))
                ) : (
                  <div className="text-center text-muted py-4">No students found.</div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 pt-0">
                  <div className="text-muted small">
                    Showing {Math.min(filteredStudents.length, (safePage - 1) * itemsPerPage + 1)} to{" "}
                    {Math.min(filteredStudents.length, safePage * itemsPerPage)} of {filteredStudents.length} students
                  </div>
                  <Pagination className="mb-0 flex-wrap">
                    <Pagination.First disabled={safePage === 1} onClick={() => setCurrentPage(1)} />
                    <Pagination.Prev disabled={safePage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} />
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <Pagination.Item key={page} active={page === safePage} onClick={() => setCurrentPage(page)}>
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next disabled={safePage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} />
                    <Pagination.Last disabled={safePage === totalPages} onClick={() => setCurrentPage(totalPages)} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Modal - Reordered sections: Personal Details > Student Preferences > School Assignment */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Form onSubmit={saveStudent}>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? "Edit Student" : "Add New Student"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-light">
            {/* Section 1: Personal Details */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                <h6 className="mb-3">Personal Details</h6>
                <Row className="g-3">
                  <Col lg={4} md={6}><Form.Label>Full Name</Form.Label><Form.Control required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Col>
                  <Col lg={4} md={6}><Form.Label>Email</Form.Label><Form.Control required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Col>
                  <Col lg={4} md={6}><Form.Label>Contact</Form.Label><Form.Control required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></Col>
                  <Col lg={4} md={6}><Form.Label>Zipcode</Form.Label><Form.Control value={form.zipcode} onChange={(e) => setForm({ ...form, zipcode: e.target.value })} /></Col>
                  <Col lg={4} md={6}><Form.Label>DOB</Form.Label><Form.Control required type="date" max={getLatestAllowedDob()} value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Gender</Form.Label>
                    <Form.Select required value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 2: Student Preferences (Moved up) */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body>
                <h6 className="mb-3">Student Preferences</h6>
                <Row className="g-3">
                  <Col lg={4} md={6}>
                    <Form.Label>Minimum School Rating</Form.Label>
                    <Form.Select
                      value={form.pref_min_rating}
                      onChange={(e) => setForm({ ...form, pref_min_rating: e.target.value })}
                    >
                      <option value="">No preference</option>
                      <option value="3">3.0+</option>
                      <option value="4">4.0+</option>
                      <option value="4.5">4.5+</option>
                    </Form.Select>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Preferred Car Type</Form.Label>
                    <Form.Select
                      value={form.preferred_car_type}
                      onChange={(e) => setForm({ ...form, preferred_car_type: e.target.value })}
                    >
                      <option value="">No preference</option>
                      <option value="hatchback">Hatchback</option>
                      <option value="sedan">Sedan</option>
                      <option value="suv">SUV</option>
                      <option value="automatic">Automatic</option>
                    </Form.Select>
                  </Col>
                  <Col lg={4} md={6} className="d-flex align-items-end">
                    <div className="w-100 d-flex flex-column gap-2">
                      <Form.Check
                        type="switch"
                        id="pref-female-driver"
                        label="Only female driver"
                        checked={form.pref_female_driver}
                        onChange={(e) => setForm({ ...form, pref_female_driver: e.target.checked })}
                      />
                      <Form.Check
                        type="switch"
                        id="pref-nearby-only"
                        label="Prioritize nearby schools"
                        checked={form.pref_nearby_only}
                        onChange={(e) => setForm({ ...form, pref_nearby_only: e.target.checked })}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 3: School Assignment (City, Course, Date, Time Slot) */}
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h6 className="mb-3">School Assignment</h6>
                <Row className="g-3">
                  <Col lg={4} md={6}>
                    <Form.Label>City</Form.Label>
                    <Form.Select
                      required
                      value={form.city_id}
                      onChange={(e) => {
                        const cityId = e.target.value;
                        setForm({ ...form, city_id: cityId, assigned_school_id: "", time_slot: "" });
                        setTimeSlots([]);
                        loadSchoolsByCity(cityId, form);
                        loadCoursesByCity(cityId);
                      }}
                    >
                      <option value="">Select city</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}{c.state_name ? `, ${c.state_name}` : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Course</Form.Label>
                    <Form.Select required value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })}>
                      <option value="">Select course</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title} {c.price != null ? `- ₹${Number(c.price).toFixed(2)}` : ""}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Driving School</Form.Label>
                    <Form.Select
                      required
                      value={form.assigned_school_id}
                      onChange={(e) => setForm({ ...form, assigned_school_id: e.target.value, time_slot: "" })}
                      disabled={!form.city_id}
                    >
                      <option value="">{form.city_id ? "Select school" : "Select city first"}</option>
                      {schools.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Estimated Start Date</Form.Label>
                    <Form.Control
                      required
                      type="date"
                      value={form.expected_start_date}
                      onChange={(e) => setForm({ ...form, expected_start_date: e.target.value, time_slot: "" })}
                    />
                  </Col>
                  <Col lg={4} md={6}>
                    <Form.Label>Estimated Time Slot</Form.Label>
                    <Form.Select
                      required
                      value={form.time_slot}
                      onChange={(e) => setForm({ ...form, time_slot: e.target.value })}
                      disabled={!form.expected_start_date || !form.assigned_school_id}
                    >
                      <option value="">Select time slot</option>
                      {form.time_slot && !timeSlots.some((slot) => slot.value === form.time_slot) && (
                        <option value={form.time_slot}>{form.time_slot} (Current)</option>
                      )}
                      {timeSlots.map((slot) => (
                        <option key={slot.value} value={slot.value} disabled={slot.booked}>
                          {formatSlotOptionLabel(slot)}
                        </option>
                      ))}
                    </Form.Select>
                    {form.expected_start_date && form.assigned_school_id && timeSlots.length > 0 && (
                      <div className="small text-muted mt-1">
                        Available seats: {timeSlots.filter((slot) => !slot.booked).length} / {timeSlots.length}
                      </div>
                    )}
                  </Col>
                  <Col lg={4} md={6} className="d-flex align-items-end">
                    <Form.Check
                      id="include-sat-sun"
                      type="checkbox"
                      label="Include Saturday/Sunday"
                      checked={form.inc_sat_sun}
                      onChange={(e) => setForm({ ...form, inc_sat_sun: e.target.checked })}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label>Admin Note</Form.Label>
                    <Form.Control as="textarea" rows={2} value={form.admin_note} onChange={(e) => setForm({ ...form, admin_note: e.target.value })} />
                  </Col>
                  <Col md={12}>
                    <div className="alert alert-info py-2 mb-0">
                      Agent can assign only city schools. Class slot and exact start date will be set by Asysta admin.
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save Student"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentStudents;
